import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { FundDetailsService } from '../../services/fund-details/fund-details';
import { CapitalService } from '../../services/capital/capital';
import { FundStatus, CapitalStatus } from '../../enums/fund-status';
import { ROUTE_PATHS } from '../../utils/route-paths.const';
import { FUND_DETAILS_TABS } from '../../constants/tab-names.const';

export const capitalGuard: CanActivateFn = (route): Observable<boolean> => {
  const fundDetailsService = inject(FundDetailsService);
  const capitalService = inject(CapitalService);
  const router = inject(Router);
  const fundUuid = route.paramMap.get('fundUuid');
  const capitalUuid = route.queryParamMap.get('capitalUuid');

  // Helper function to navigate to main-details with capital tab
  const navigateToCapitalTab = (): void => {
    router.navigate(
      [ROUTE_PATHS.FUND_MANAGEMENT, fundUuid, ROUTE_PATHS.DETAILS],
      {
        queryParams: { tab: FUND_DETAILS_TABS.FINANCIALS },
      }
    );
  };

  if (!fundUuid) {
    router.navigate([ROUTE_PATHS.ROOT]);
    return of(false);
  }

  // Check if navigation is from main-details (bypass guard in this case)
  const navigation = router.currentNavigation();
  const bypassGuard = navigation?.extras?.state?.['bypassGuard'];

  if (bypassGuard) {
    return of(true);
  }

  return fundDetailsService.getFundDetails(fundUuid).pipe(
    switchMap(fundDetails => {
      if (fundDetails.state?.value !== FundStatus.APPROVED) {
        router.navigate([
          ROUTE_PATHS.FUND_MANAGEMENT,
          fundUuid,
          ROUTE_PATHS.DETAILS,
        ]);
        return of(false);
      }

      const latestCapitalBaseState =
        fundDetails.latest_capital_base_state?.value;

      // If capitalUuid is present, fetch capital base details and validate state
      if (capitalUuid) {
        return capitalService.getCapitalBaseDetails(fundUuid, capitalUuid).pipe(
          map(capitalResponse => {
            const capitalState = capitalResponse.data.state?.value;

            if (capitalState !== CapitalStatus.PENDING_CLARIFICATION) {
              navigateToCapitalTab();
              return false;
            }

            return true;
          }),
          catchError(() => {
            navigateToCapitalTab();
            return of(false);
          })
        );
      } else {
        // If no capitalUuid, validate that latest_capital_base_state is null, APPROVED, or REJECTED
        if (
          latestCapitalBaseState !== undefined &&
          latestCapitalBaseState !== CapitalStatus.APPROVED &&
          latestCapitalBaseState !== CapitalStatus.REJECTED
        ) {
          navigateToCapitalTab();
          return of(false);
        }

        return of(true);
      }
    }),
    catchError(() => {
      router.navigate([ROUTE_PATHS.ROOT]);
      return of(false);
    })
  );
};
